import type {
  AllowanceResponse,
  ApproveResponse,
  OneInchErrorResponse,
  SwapResponse,
} from '@/services/oneinch-v2-service2/types'
import { use1InchStore } from '@/stores/oneInchStore'
import { Web3 } from 'web3'
import { ERC20_ABI } from '@/utils/oneinch'

export class SwapError extends Error {
  public readonly response?: OneInchErrorResponse

  constructor(response: OneInchErrorResponse) {
    super(`swap error: ${response}`)
    this.response = response
    Object.setPrototypeOf(this, SwapError.prototype)
  }
}

export function Web3Error() {
  const err = Error('web3 error')
  err.name = 'Web3Error'
  return err
}

export const useWeb3 = () => {
  const urlIndex = 0
  let web3 = null as Web3 | null

  const oneInchStore = use1InchStore()
  const initWeb3 = (urls: string[]) => {
    web3 = new Web3(urls[urlIndex])
  }

  const getPublicKey = (xprv: string) => {
    if (!web3) {
      throw Web3Error()
    }
    return web3.eth.accounts.privateKeyToAccount(xprv).address.toLowerCase()
  }

  const getTokenDecimals = async (tokenAddress: string) => {
    if (!web3) {
      throw Web3Error()
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const contract = new web3.eth.Contract(ERC20_ABI as any, tokenAddress)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const decimals = await (contract.methods as any).decimals().call()
    return Number(decimals as unknown as string)
  }

  const getTokenBalance = async (tokenAddress: string, walletAddress: string) => {
    if (!web3) {
      throw Web3Error()
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const contract = new web3.eth.Contract(ERC20_ABI as any, tokenAddress)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const balance = await (contract.methods as any).balanceOf(walletAddress).call()
    return BigInt(balance as unknown as string)
  }

  const swap = async (
    chain: string,
    tokenFrom: string,
    tokenTo: string,
    amount: bigint,
    slippage: number,
    publicKey: string,
    privateKey: string,
  ) => {
    const allowance_ = await oneInchStore.checkAllowance(chain, tokenFrom, publicKey)

    if (allowance_ && 'error' in allowance_) {
      const allowanceError = allowance_ as OneInchErrorResponse
      throw new SwapError(allowanceError)
    }
    const allowance = allowance_ as AllowanceResponse

    if (BigInt(allowance?.allowance ?? 0) === 0n) {
      const approve_ = await oneInchStore.approve(chain, tokenFrom)
      if (approve_ && 'error' in approve_) {
        const approveError = approve_ as OneInchErrorResponse
        throw new SwapError(approveError)
      }
      const approve = approve_ as ApproveResponse

      if (!approve?.data) {
        console.error(`approve?.data is null or undefined`)
        return
      }
      const approveResult = await sendRaw(
        privateKey,
        publicKey,
        approve?.to,
        approve?.data,
        approve?.gasPrice,
        null,
        '0',
      )
      console.log(approveResult?.blockHash)
    }

    const swap_ = await oneInchStore.fetchSwapCalldata(
      chain,
      tokenFrom,
      tokenTo,
      publicKey,
      amount,
      slippage.toString(),
    )

    if (swap_ && 'statusCode' in swap_) {
      const swapError = swap_ as OneInchErrorResponse
      throw new SwapError(swapError)
    }

    const swap = swap_ as SwapResponse
    if (!swap?.tx?.to) {
      console.error('swap?.tx.to is null')
      return
    }
    if (!swap?.tx?.gas) {
      console.error('swap?.tx.gas is null')
      return
    }
    const swapResult = await sendRaw(
      privateKey,
      publicKey,
      swap?.tx.to,
      swap?.tx.data,
      swap?.tx.gasPrice,
      swap?.tx.gas,
      swap?.tx.value,
    )
    return {
      swap: swap,
      result: swapResult,
    }
  }

  const sendRaw = async (
    xprv: string,
    xpub: string,
    to: string,
    data: string,
    gasPrice: string,
    gas: string | null | number,
    value: string,
  ) => {
    if (!web3) {
      throw Web3Error()
    }
    const nonce = await web3.eth.getTransactionCount(xpub, 'pending')
    const txObject = {
      nonce: web3.utils.toHex(nonce),
      from: xpub,
      to: to,
      value: value,
      data: data,
      chainId: await web3.eth.getChainId(),
    } as Record<string, string | bigint | number>
    if (!gas) {
      txObject['gas'] = web3.utils.toHex(await web3.eth.estimateGas(txObject))
    } else {
      txObject['gas'] = gas
    }
    txObject['gasPrice'] = gasPrice
    const signedTx = await web3.eth.accounts.signTransaction(txObject, xprv)
    const receipt = await web3.eth.sendSignedTransaction(signedTx.rawTransaction)
    return receipt
  }

  return {
    web3,
    swap,
    sendRaw,
    getPublicKey,
    initWeb3,
    getTokenDecimals,
    getTokenBalance,
  }
}
